import { isNullOrUndefined, isUndefined } from '@syncfusion/ej2-base';
import { getValue, createElement, extend } from '@syncfusion/ej2-base';
import * as cls from '../base/css-constants';
import { parentsUntil, formatString, getIndex } from '../base/utils';
import { Dialog } from '@syncfusion/ej2-popups';
import { RadioButton } from '@syncfusion/ej2-buttons';
/**
 * File for handling connector line edit operation in Gantt.
 *
 */
export class ConnectorLineEdit {
    constructor(ganttObj) {
        /**
         * @private
         */
        this.validationPredecessor = null;
        /** @private */
        this.confirmPredecessorDialog = null;
        /** @private */
        this.predecessorIndex = null;
        /** @private */
        this.childRecord = null;
        this.validatedId = [];
        this.isPublicDependencyDelete = false;
        this.parent = ganttObj;
        this.dateValidateModule = this.parent.dateValidationModule;
        this.parent.on('initPredessorDialog', this.initPredecessorValidationDialog, this);
    }
    /**
     * To update connector line edit element.
     *
     * @param {PointerEvent} e .
     * @returns {void} .
     * @private
     */
    updateConnectorLineEditElement(e) {
        const element = this.getConnectorLineHoverElement(e.target);
        if (!getValue('editModule.taskbarEditModule.taskBarEditAction', this.parent)) {
            this.highlightConnectorLineElements(element);
        }
    }
    /**
     * To get hovered connector line element.
     *
     * @param {EventTarget} target .
     * @returns {void} .
     * @private
     */
    getConnectorLineHoverElement(target) {
        const isOnLine = parentsUntil(target, cls.connectorLineSVG);
        const isArrow = parentsUntil(target, cls.connectorLineArrow);
        const isCriticalLine = parentsUntil(target, cls.criticalConnectorLineSVG);
        const isCriticalArrow = parentsUntil(target, cls.criticalConnectorArrowSVG);
        if (isOnLine || isArrow || isCriticalLine || isCriticalArrow) {
            return parentsUntil(target, cls.connectorLineContainer);
        }
        else {
            return null;
        }
    }
    /**
     * To highlight connector line while hover.
     *
     * @param {Element} element .
     * @returns {void} .
     * @private
     */
    highlightConnectorLineElements(element) {
        if (element) {
            if (element !== this.connectorLineElement) {
                this.removeHighlight();
                this.addHighlight(element);
            }
        }
        else {
            this.removeHighlight();
        }
    }
    /**
     * To add connector line highlight class.
     *
     * @param {Element} element .
     * @returns {void} .
     * @private
     */
    addHighlight(element) {
        this.connectorLineElement = element;
        const pathElement = element.querySelector('.' + cls.connectorLineSVG);
        if (pathElement) {
            pathElement.setAttribute('stroke-width', (this.parent.connectorLineModule['lineStroke'] + 1).toString());
        }
    }
    /**
     * To remove connector line highlight class.
     *
     * @returns {void} .
     * @private
     */
    removeHighlight() {
        if (this.connectorLineElement) {
            const pathElement = this.connectorLineElement.querySelector('.' + cls.connectorLineSVG);
            if (pathElement) {
                pathElement.setAttribute('stroke-width', (this.parent.connectorLineModule['lineStroke']).toString());
            }
            this.connectorLineElement = null;
        }
    }
    /**
     * To remove connector line highlight class.
     *
     * @param {IGanttData[]} records .
     * @returns {DocumentFragment} .
     * @private
     */
    getEditedConnectorLineString(records) {
        let ganttRecord;
        let predecessorsCollection;
        let parentGanttRecord;
        let childGanttRecord;
        let connectorObj;
        const idSet = new Set();
        const lineFragment = document.createDocumentFragment();
        for (let count = 0; count < records.length; count++) {
            ganttRecord = records[count];
            predecessorsCollection = ganttRecord.ganttProperties.predecessor;
            if (!predecessorsCollection) {
                continue;
            }
            for (let predecessorCount = 0; predecessorCount < predecessorsCollection.length; predecessorCount++) {
                const predecessor = predecessorsCollection[predecessorCount];
                const from = 'from';
                const to = 'to';
                const connectorLineId = 'parent' + predecessor[from] + 'child' + predecessor[to];
                this.parent.connectorLineModule.removeConnectorLineById(connectorLineId);
                parentGanttRecord = this.parent.connectorLineModule.getRecordByID(predecessor[from]);
                childGanttRecord = this.parent.connectorLineModule.getRecordByID(predecessor[to]);
                if ((!this.parent.allowParentDependency && ((parentGanttRecord && parentGanttRecord.expanded) ||
                    (childGanttRecord && childGanttRecord.expanded))) ||
                    (this.parent.allowParentDependency && (parentGanttRecord || childGanttRecord))) {
                    connectorObj = this.parent.predecessorModule.updateConnectorLineObject(parentGanttRecord, childGanttRecord, predecessor);
                    if (!isNullOrUndefined(connectorObj) && !idSet.has(connectorObj.connectorLineId)) {
                        const lineElement = this.parent.connectorLineModule.getConnectorLineTemplate(connectorObj);
                        idSet.add(connectorObj.connectorLineId);
                        lineFragment.appendChild(lineElement);
                    }
                }
            }
        }
        return lineFragment;
    }
    /**
     * Tp refresh connector lines of edited records
     *
     * @param {IGanttData[]} editedRecord .
     * @returns {void} .
     * @private
     */
    refreshEditedRecordConnectorLine(editedRecord) {
        this.parent.connectorLineModule.removePreviousConnectorLines(this.parent.previousRecords);
        this.parent.connectorLineModule.expandedRecords = this.parent.virtualScrollModule && this.parent.enableVirtualization ?
            this.parent.updatedRecords : this.parent.expandedRecords;
        const editedConnectorElement = this.getEditedConnectorLineString(editedRecord);
        if (editedConnectorElement) {
            this.parent.connectorLineModule.svgObject.appendChild(editedConnectorElement);
        }
    }
    idFromPredecessor(pre) {
        const preArray = pre.split(',');
        const preIdArray = [];
        const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        /* eslint-disable */
        const suffixRegex = /\s*([A-Z]{1,2})([+-]\d*\.?\d+\s*(?:days|day|hours|hour|minutes|minute|[DHM])?)?$/i;
        const lagRegex = /([+-]\d*\.?\d+\s*(?:days|day|hours|hour|minutes|minute|[DHM])?)$/i;
        const ids = this.parent.ids;
        for (let j = 0; j < preArray.length; j++) {
            const predecessor = preArray[j].trim();
            let id = predecessor;
            if (guidRegex.test(predecessor.substring(0, 36))) {
                id = predecessor.substring(0, 36);
            }
            else {
                if (ids.indexOf(predecessor) !== -1) {
                    id = predecessor;
                }
                else {
                    const match = predecessor.match(suffixRegex);
                    if (match) {
                        const prefix = predecessor.substring(0, predecessor.length - match[0].length);
                        if (ids.indexOf(prefix) !== -1) {
                            id = prefix;
                        }
                    }
                    if (id === predecessor) {
                        const lagMatch = predecessor.match(lagRegex);
                        if (lagMatch) {
                            const prefix = predecessor.substring(0, predecessor.length - lagMatch[0].length);
                            if (ids.indexOf(prefix) !== -1) {
                                id = prefix;
                            }
                        }
                    }
                    if (id === predecessor) {
                        const splitChar = predecessor.includes('+') ? '+' : '-';
                        const parts = predecessor.split(splitChar);
                        if (parts[0] && ids.indexOf(parts[0]) !== -1) {
                            id = parts[0];
                        }
                    }
                }
            }
            preIdArray.push(id);
        }
        return preIdArray;
    }
    predecessorValidation(predecessor, record) {
        const recordId = record.rowUniqueID;
        let currentId;
        let currentRecord;
        for (let count = 0; count < predecessor.length; count++) {
            currentId = predecessor[count];
            const visitedIdArray = [];
            const predecessorCollection = predecessor.slice(0);
            predecessorCollection.splice(count, 1);
            while (currentId !== null) {
                const currentIdArray = [];
                if (visitedIdArray.indexOf(currentId) === -1) {
                    //Predecessor id not in records collection
                    if (isNullOrUndefined(this.parent.connectorLineModule.getRecordByID(currentId))) {
                        return false;
                    }
                    currentRecord = this.parent.connectorLineModule.getRecordByID(currentId).ganttProperties;
                    if (!isNullOrUndefined(currentRecord.predecessor) && currentRecord.predecessor.length > 0) {
                        currentRecord.predecessor.forEach((value) => {
                            if (currentRecord.rowUniqueID.toString() !== value.from) {
                                currentIdArray.push(value.from.toString());
                            }
                        });
                    }
                    /* eslint-disable-next-line */
                    if (recordId.toString() === currentRecord.rowUniqueID.toString() || currentIdArray.indexOf(recordId.toString()) !== -1) {
                        return false;
                    }
                    visitedIdArray.push(currentId);
                    if (!isNullOrUndefined(currentRecord.predecessor) && currentRecord.predecessor.length > 0) {
                        currentId = currentRecord.predecessor[0].from;
                    }
                    else {
                        break;
                    }
                }
                else {
                    break;
                }
            }
        }
        return true;
    }
    /**
     * To validate predecessor relations
     *
     * @param {IGanttData} ganttRecord .
     * @param {string} predecessorString .
     * @returns {boolean} .
     * @private
     */
    validatePredecessorRelation(ganttRecord, predecessorString) {
        let flag = true;
        const recordId = this.parent.viewType === 'ResourceView' ? ganttRecord.ganttProperties.taskId
            : ganttRecord.ganttProperties.rowUniqueID;
        let predecessorIdArray;
        let currentId;
        if (!isNullOrUndefined(predecessorString) && predecessorString.length > 0) {
            predecessorIdArray = this.idFromPredecessor(predecessorString);
            for (let count = 0; count < predecessorIdArray.length; count++) {
                //Check edited item has parent item in predecessor collection
                if (!this.parent.allowParentDependency) {
                    const checkParent = this.checkParentRelation(ganttRecord, predecessorIdArray);
                    if (!checkParent) {
                        return false;
                    }
                }
                else {
                    if (parseInt(predecessorIdArray[predecessorIdArray.length - 1], 10) !== ganttRecord[this.parent.taskFields.id]) {
                        const num = this.parent.ids.indexOf(predecessorIdArray[predecessorIdArray.length - 1]);
                        const fromRecord = this.parent.currentViewData[num];
                        if (fromRecord && ganttRecord) {
                            flag = this.parent.predecessorModule.validateParentPredecessor(fromRecord, ganttRecord);
                        }
                    }
                }
                // Check if predecessor exist more then one
                const tempIdArray = predecessorIdArray.slice(0);
                const checkArray = [];
                let countFlag = true;
                tempIdArray.forEach((value) => {
                    if (checkArray.indexOf(value) === -1) {
                        checkArray.push(value);
                    }
                    else {
                        countFlag = false;
                    }
                });
                if (!countFlag) {
                    return false;
                }
                //Cyclick check
                currentId = predecessorIdArray[count];
                const visitedIdArray = [];
                const predecessorCollection = predecessorIdArray.slice(0);
                predecessorCollection.splice(count, 1);
                while (currentId !== null) {
                    const currentIdArray = [];
                    let currentIdIndex;
                    let currentRecord;
                    if (visitedIdArray.indexOf(currentId) === -1) {
                        //Predecessor id not in records collection
                        if (isNullOrUndefined(this.parent.connectorLineModule.getRecordByID(currentId.toString()))) {
                            return false;
                        }
                        currentRecord = this.parent.connectorLineModule.getRecordByID(currentId.toString()).ganttProperties;
                        //  let currentPredecessor='';
                        if (!isNullOrUndefined(currentRecord.predecessor) && currentRecord.predecessor.length > 0) {
                            currentRecord.predecessor.forEach((value, index) => {
                                if (currentRecord.rowUniqueID.toString() !== value.from) {
                                    currentIdArray.push(value.from.toString());
                                    currentIdIndex = index;
                                }
                            });
                            //    currentPredecessor=currentRecord.predecessor[0].from
                        }
                        if (recordId.toString() === currentRecord.rowUniqueID.toString() ||
                            currentIdArray.indexOf(recordId.toString()) !== -1) {
                            //cycylic occurs//break;
                            return false;
                        }
                        visitedIdArray.push(currentId);
                        if (!isNullOrUndefined(currentRecord.predecessor) && currentRecord.predecessor.length > 0) {
                            let result;
                            if (currentIdArray.length > 1) {
                                result = this.predecessorValidation(currentIdArray, ganttRecord.ganttProperties);
                            }
                            else if (currentIdArray.length === 1) {
                                currentId = currentRecord.predecessor[currentIdIndex].from;
                            }
                            if (result === false) {
                                return false;
                            }
                        }
                        else {
                            break;
                        }
                    }
                    else {
                        break;
                    }
                }
            }
        }
        return flag;
    }
    /**
     * To add dependency for Task
     *
     * @param {IGanttData} ganttRecord .
     * @param {string} predecessorString .
     * @returns {void} .
     * @private
     */
    addPredecessor(ganttRecord, predecessorString) {
        const tempPredecessorString = isNullOrUndefined(ganttRecord.ganttProperties.predecessorsName) ||
            ganttRecord.ganttProperties.predecessorsName === '' ?
            predecessorString : (ganttRecord.ganttProperties.predecessorsName + ',' + predecessorString);
        this.updatePredecessorHelper(ganttRecord, tempPredecessorString);
    }
    /**
     * To remove dependency from task
     *
     * @param {IGanttData} ganttRecord .
     * @returns {void} .
     * @private
     */
    removePredecessor(ganttRecord) {
        this.isPublicDependencyDelete = true;
        this.updatePredecessorHelper(ganttRecord, null);
    }
    /**
     * To modify current dependency values of Task
     *
     * @param {IGanttData} ganttRecord .
     * @param {string} predecessorString .
     * @param {ITaskbarEditedEventArgs} editedArgs .
     * @returns {boolean} .
     * @private
     */
    updatePredecessor(ganttRecord, predecessorString, editedArgs) {
        return this.updatePredecessorHelper(ganttRecord, predecessorString, editedArgs);
    }
    updatePredecessorHelper(ganttRecord, predecessorString, editedArgs) {
        if (isUndefined(predecessorString) || this.validatePredecessorRelation(ganttRecord, predecessorString)) {
            this.parent.isOnEdit = true;
            let predecessorCollection = [];
            if (!isNullOrUndefined(predecessorString) && predecessorString !== '') {
                predecessorCollection = this.parent.predecessorModule.calculatePredecessor(predecessorString, ganttRecord);
            }
            this.parent.setRecordValue('predecessor', predecessorCollection, ganttRecord.ganttProperties, true);
            const stringValue = this.parent.predecessorModule.getPredecessorStringValue(ganttRecord);
            this.parent.setRecordValue('predecessorsName', stringValue, ganttRecord.ganttProperties, true);
            this.parent.setRecordValue('taskData.' + this.parent.taskFields.dependency, stringValue, ganttRecord);
            this.parent.setRecordValue(this.parent.taskFields.dependency, stringValue, ganttRecord);
            const args = {};
            args.action = editedArgs && editedArgs.action && editedArgs.action === 'CellEditing' ? editedArgs.action : ((this.parent.contextMenuModule && this.parent.contextMenuModule['isContextMenuDependencyDelete']) ||
                this.isPublicDependencyDelete) ? 'DeleteConnectorLine' : 'DrawConnectorLine';
            args.data = ganttRecord;
            this.parent.editModule.initiateUpdateAction(args);
            return true;
        }
        else {
            if (ganttRecord.taskData[this.parent.taskFields.dependency]) {
                ganttRecord.taskData[this.parent.taskFields.dependency] = null;
            }
            const err = `${predecessorString} is an invalid relation for task ${this.parent.taskFields.id}. Kindly ensure the ${this.parent.taskFields.dependency} field contains only valid predecessor relations.`;
            this.parent.trigger('actionFailure', { error: err });
            return false;
        }
    }
    checkParentRelation(ganttRecord, predecessorIdArray) {
        const editingData = ganttRecord;
        const checkParent = true;
        if (editingData && editingData.parentItem) {
            if (predecessorIdArray.indexOf(editingData.parentItem.taskId.toString()) !== -1) {
                return false;
            }
        }
        for (let p = 0; p < predecessorIdArray.length; p++) {
            const record = this.parent.currentViewData.filter((item) => {
                return item && item.ganttProperties.rowUniqueID.toString() === predecessorIdArray[p].toString();
            });
            if (record[0] && record[0].hasChildRecords) {
                return false;
            }
        }
        return checkParent;
    }
    initPredecessorValidationDialog() {
        if (this.parent.taskFields.dependency && this.parent.isInPredecessorValidation) {
            const dialogElement = createElement('div', {
                id: this.parent.element.id + '_dialogValidationRule'
            });
            this.parent.element.appendChild(dialogElement);
            this.renderValidationDialog();
        }
    }
    /**
     * To render validation dialog
     *
     * @returns {void} .
     * @private
     */
    renderValidationDialog() {
        const taskFields = this.parent.taskFields;
        const validationHeader = (taskFields.constraintType && taskFields.constraintDate)
            ? this.parent.localeObj.getConstant('schedulingConflicts')
            : this.parent.localeObj.getConstant('validateEditing');
        const containerId = this.parent.element.id + '_dialogValidationRule';
        let container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = '';
            container.style.zIndex = '2000';
        }
        else {
            container = document.createElement('div');
            container.id = containerId;
            container.style.zIndex = '2000'; // Set z-index here
            document.body.appendChild(container);
        }
        const validationDialog = new Dialog({
            header: validationHeader,
            isModal: true,
            enableRtl: this.parent.enableRtl,
            visible: false,
            width: '50%',
            showCloseIcon: true,
            zIndex: 2000,
            close: this.validationDialogClose.bind(this),
            content: '',
            buttons: [
                {
                    click: this.validationDialogOkButton.bind(this),
                    buttonModel: {
                        content: this.parent.localeObj.getConstant('okText'),
                        isPrimary: true
                    }
                },
                {
                    click: this.validationDialogCancelButton.bind(this),
                    buttonModel: {
                        content: this.parent.localeObj.getConstant('cancel')
                    }
                }
            ],
            target: document.body,
            animationSettings: { effect: 'None' }
        });
        validationDialog.isStringTemplate = true;
        validationDialog.appendTo('#' + containerId);
        this.parent.validationDialogElement = validationDialog;
    }
    validationDialogOkButton() {
        const currentArgs = this.parent.currentEditedArgs;
        const addOffsetCheckbox = document.getElementById(this.parent.element.id + '_ValidationAddlineOffset');
        const removeLineCheckbox = document.getElementById(this.parent.element.id + '_ValidationRemoveline');
        const cancelCheckbox = document.getElementById(this.parent.element.id + '_ValidationCancel');
        const removeConstraintCheckbox = document.getElementById(this.parent.element.id + '_RemoveConstraint');
        const removeDependencyCheckbox = document.getElementById(this.parent.element.id + '_RemoveDependency');
        const cancelChangeCheckbox = document.getElementById(this.parent.element.id + '_CancelChange');
        const violationArgs = this.parent.editModule['violationArgs'];
        const modifiedTask = violationArgs['matchedModifiedTask'];
        const validationArgs = violationArgs['args'];
        const pairedTask = violationArgs['matchedParedRecord'] || null;
        if (removeConstraintCheckbox !== null && removeConstraintCheckbox.checked) {
            modifiedTask.ganttProperties.constraintType = 4;
            this.parent.constraintViolationType = '';
            this.parent.editModule.initiateSaveAction(validationArgs);
        }
        else if (removeDependencyCheckbox !== null && removeDependencyCheckbox.checked) {
            let matchedPredecessors = [];
            const currentTaskId = pairedTask
                ? pairedTask.ganttProperties.taskId
                : validationArgs.data.ganttProperties.taskId;
            if (modifiedTask && Array.isArray(modifiedTask.ganttProperties.predecessor)) {
                for (const predecessor of modifiedTask.ganttProperties.predecessor) {
                    if (predecessor.from.toString() === currentTaskId.toString()) {
                        matchedPredecessors = [predecessor];
                        break;
                    }
                }
            }
            this.parent.editModule.reUpdatePreviousRecords(undefined, undefined, modifiedTask.uniqueID);
            if (modifiedTask.parentItem && modifiedTask.parentItem.taskId) {
                const parentItem = this.parent.getRecordByID(modifiedTask.parentItem.taskId);
                this.parent.dataOperation.updateParentItems(parentItem, true);
            }
            this.removePredecessors(modifiedTask, matchedPredecessors);
            this.parent.editModule.initiateSaveAction(validationArgs);
        }
        else if (cancelChangeCheckbox !== null && cancelChangeCheckbox.checked) {
            currentArgs.validateMode.respectLink = true;
            this.applyPredecessorOption();
        }
        const noPrimaryActionTaken = !(removeConstraintCheckbox !== null && removeConstraintCheckbox.checked) &&
            !(removeDependencyCheckbox !== null && removeDependencyCheckbox.checked) &&
            !(cancelChangeCheckbox !== null && cancelChangeCheckbox.checked);
        if (noPrimaryActionTaken) {
            currentArgs.validateMode.preserveLinkWithEditing =
                addOffsetCheckbox !== null && addOffsetCheckbox.checked;
            currentArgs.validateMode.removeLink =
                removeLineCheckbox !== null && removeLineCheckbox.checked;
            currentArgs.validateMode.respectLink =
                cancelCheckbox !== null && cancelCheckbox.checked;
            this.applyPredecessorOption();
        }
        this.parent.hideSpinner();
        this.parent.validationDialogElement.hide();
    }
    validationDialogCancelButton() {
        this.parent.constraintViolationType = '';
        this.parent.currentEditedArgs.validateMode.respectLink = true;
        this.applyPredecessorOption();
        this.parent.hideSpinner();
        this.parent.validationDialogElement.hide();
    }
    validationDialogClose(e) {
        this.parent.constraintViolationType = '';
        if (getValue('isInteracted', e)) {
            this.parent.currentEditedArgs.validateMode.respectLink = true;
            this.applyPredecessorOption();
            this.parent.hideSpinner();
        }
    }
    /**
     * Validate and apply the predecessor option from validation dialog
     *
     * @returns {void} .
     * @private
     */
    applyPredecessorOption() {
        const args = this.parent.currentEditedArgs;
        const ganttRecord = args.data;
        if (args.validateMode.respectLink) {
            this.parent.editModule.reUpdatePreviousRecords();
            this.parent.chartRowsModule.refreshRecords([args.data]);
        }
        else if (args.validateMode.removeLink) {
            this.checkChildRecords(ganttRecord);
            this.parent.editModule.updateEditedTask(args.editEventArgs);
        }
        else if (args.validateMode.preserveLinkWithEditing) {
            let connectedTaskId;
            if (this.parent.updateOffsetOnTaskbarEdit) {
                const taskId = ganttRecord.ganttProperties.taskId.toString();
                if (ganttRecord.ganttProperties.predecessor) {
                    ganttRecord.ganttProperties.predecessor.forEach((predecessor) => {
                        if (taskId === predecessor.from) {
                            connectedTaskId = predecessor.to;
                            return;
                        }
                    });
                }
            }
            this.parent.editModule.updateEditedTask(args.editEventArgs);
            let processedPredecessor = [];
            this.processPredecessor(connectedTaskId, processedPredecessor);
            processedPredecessor = [];
        }
    }
    compareArrays(arr1, arr2) {
        if (arr1.length !== arr2.length) {
            return false;
        }
        const str1 = JSON.stringify(arr1);
        const str2 = JSON.stringify(arr2);
        return str1 === str2;
    }
    processPredecessor(parentId, processedPredecessor) {
        if (parentId) {
            const record = this.parent.getRecordByID(parentId);
            if (record && record.ganttProperties && record.ganttProperties.predecessor) {
                this.parent.predecessorModule['validatedOffsetIds'] = [];
                this.parent.predecessorModule['calculateOffset'](record);
                let isIdInclude = true;
                /* eslint-disable-next-line */
                const matchedObject = this.validatedId.find((item) => item.id === record.ganttProperties.taskId);
                if (matchedObject) {
                    const predecessorArray = matchedObject.value;
                    const areArraysEqual = this.compareArrays(predecessorArray, record.ganttProperties.predecessor);
                    if (areArraysEqual) {
                        isIdInclude = false;
                    }
                }
                const predecessors = record.ganttProperties.predecessor;
                predecessors.forEach((predecessor) => {
                    if (record.ganttProperties.taskId === predecessor.from && isIdInclude) {
                        if (processedPredecessor && processedPredecessor.indexOf(predecessor.to) === -1) {
                            this.processPredecessor(predecessor.to, processedPredecessor);
                            processedPredecessor.push(predecessor.to);
                        }
                    }
                });
            }
        }
    }
    checkChildRecords(ganttRecord) {
        const matchingPredecessors = [];
        const currentTaskId = ganttRecord.ganttProperties.taskId;
        const allPredecessors = ganttRecord.ganttProperties.predecessor;
        if (isNullOrUndefined(allPredecessors)) {
            this.validationPredecessor = allPredecessors;
        }
        else {
            for (let i = 0; i < allPredecessors.length; i++) {
                const predecessorLink = allPredecessors[i];
                if (predecessorLink.to.toString() === currentTaskId.toString()) {
                    matchingPredecessors.push(predecessorLink);
                }
            }
            this.validationPredecessor = matchingPredecessors;
        }
        if (!isNullOrUndefined(this.validationPredecessor)) {
            this.removePredecessors(ganttRecord, this.validationPredecessor);
        }
        if (ganttRecord.childRecords.length > 0) {
            for (let i = 0; i < ganttRecord.childRecords.length; i++) {
                const childRecord = ganttRecord.childRecords[i];
                this.validationPredecessor = childRecord.ganttProperties.predecessor;
                if (!isNullOrUndefined(this.validationPredecessor)) {
                    this.removePredecessors(childRecord, this.validationPredecessor);
                }
                if (childRecord.childRecords.length > 0) {
                    this.checkChildRecords(childRecord);
                }
            }
        }
        else if (!isNullOrUndefined(ganttRecord.parentItem)) {
            const parentRecord = this.parent.getRecordByID(ganttRecord.parentItem.taskId);
            this.validationPredecessor = parentRecord.ganttProperties.predecessor;
            this.removePredecessors(parentRecord, this.validationPredecessor);
        }
    }
    /**
     * Update predecessor value with user selection option in predecessor validation dialog
     *
     * @param {IGanttData} ganttRecord .
     * @param {IPredecessor[]} predecessor .
     * @returns {void} .
     */
    removePredecessors(ganttRecord, predecessor) {
        const prevPredecessor = extend([], [], ganttRecord.ganttProperties.predecessor, true);
        if (isNullOrUndefined(predecessor)) {
            return;
        }
        const preLength = predecessor.length;
        for (let i = 0; i < preLength; i++) {
            const parentGanttRecord = this.parent.connectorLineModule.getRecordByID(predecessor[i].from);
            const parentPredecessor = extend([], [], parentGanttRecord.ganttProperties.predecessor, true);
            const index = getIndex(predecessor[i], 'from', prevPredecessor, 'to');
            prevPredecessor.splice(index, 1);
            const parentIndex = getIndex(predecessor[parseInt(i.toString(), 10)], 'from', parentPredecessor, 'to');
            parentPredecessor.splice(parentIndex, 1);
            this.parent.setRecordValue('predecessor', parentPredecessor, parentGanttRecord.ganttProperties, true);
        }
        if (prevPredecessor.length !== ganttRecord.ganttProperties.predecessor.length) {
            this.parent.setRecordValue('predecessor', prevPredecessor, ganttRecord.ganttProperties, true);
            const predecessorString = this.parent.predecessorModule.getPredecessorStringValue(ganttRecord);
            this.parent.setRecordValue('predecessorsName', predecessorString, ganttRecord.ganttProperties, true);
            this.parent.setRecordValue('taskData.' + this.parent.taskFields.dependency, predecessorString, ganttRecord);
            this.parent.setRecordValue(this.parent.taskFields.dependency, predecessorString, ganttRecord);
        }
    }
    formatViolationType(violationType) {
        return violationType.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    updateZIndex(ganttObj) {
        if (ganttObj &&
            ganttObj.editModule &&
            ganttObj.editModule.dialogModule &&
            ganttObj.editModule.dialogModule.dialog &&
            ganttObj.editModule.dialogModule.dialog.style &&
            ganttObj.editModule.dialogModule.dialog.style.zIndex !== '') {
            const zIndex = parseInt(ganttObj.editModule.dialogModule.dialog.style.zIndex, 10);
            const validationElement = ganttObj.validationDialogElement &&
                ganttObj.validationDialogElement.element;
            if (!isNaN(zIndex) && validationElement) {
                const newZIndex = (zIndex + 1).toString();
                validationElement.style.zIndex = newZIndex;
                if (validationElement.parentElement) {
                    validationElement.parentElement.style.zIndex = newZIndex;
                }
            }
        }
    }
    /**
     * To open predecessor validation dialog
     *
     * @param {object} args .
     * @returns {void} .
     * @private
     */
    openValidationDialog(args) {
        const ganttObj = this.parent;
        const contentTemplate = this.validationDialogTemplate(args);
        ganttObj.validationDialogElement.setProperties({ content: contentTemplate });
        const contentId = ganttObj.element.id + '_dialogValidationRule_dialog-content';
        const contentElement = ganttObj.validationDialogElement.element.querySelector('#' + contentId);
        contentElement.style.paddingTop = '10px';
        const headerId = ganttObj.element.id + '_dialogValidationRule_dialog-header';
        const headerElement = ganttObj.validationDialogElement.element.querySelector('#' + headerId);
        headerElement.style.padding = '8px 16px';
        this.updateZIndex(ganttObj);
        ganttObj.validationDialogElement.show();
    }
    /**
     * Predecessor link validation dialog template
     *
     * @param {object} args .
     * @returns {HTMLElement} .
     * @private
     */
    validationDialogTemplate(args) {
        const ganttId = this.parent.element.id;
        const contentdiv = createElement('div', {
            className: 'e-ValidationContent'
        });
        const taskData = getValue('task', args);
        const parenttaskData = getValue('parentTask', args);
        let violationType = getValue('violationType', args);
        const recordName = taskData.ganttProperties.taskName;
        const dateFormat = !isNullOrUndefined(this.parent.dateFormat) ? this.parent.dateFormat : 'M/d/yyyy h:mm a';
        const recordNewStartDate = this.parent.getFormatedDate(taskData.ganttProperties.startDate, dateFormat);
        const parentName = parenttaskData.ganttProperties.taskName;
        const recordArgs = [recordName, parentName];
        let topContentText = '';
        if (violationType !== 'MustStartOn' &&
            violationType !== 'MustFinishOn' &&
            violationType !== 'StartNoLaterThan' &&
            violationType !== 'FinishNoLaterThan') {
            if (violationType === 'taskBeforePredecessor_FS') {
                topContentText = this.parent.localeObj.getConstant('taskBeforePredecessor_FS');
            }
            else if (violationType === 'taskAfterPredecessor_FS') {
                topContentText = this.parent.localeObj.getConstant('taskAfterPredecessor_FS');
            }
            else if (violationType === 'taskBeforePredecessor_SS') {
                topContentText = this.parent.localeObj.getConstant('taskBeforePredecessor_SS');
            }
            else if (violationType === 'taskAfterPredecessor_SS') {
                topContentText = this.parent.localeObj.getConstant('taskAfterPredecessor_SS');
            }
            else if (violationType === 'taskBeforePredecessor_FF') {
                topContentText = this.parent.localeObj.getConstant('taskBeforePredecessor_FF');
            }
            else if (violationType === 'taskAfterPredecessor_FF') {
                topContentText = this.parent.localeObj.getConstant('taskAfterPredecessor_FF');
            }
            else if (violationType === 'taskBeforePredecessor_SF') {
                topContentText = this.parent.localeObj.getConstant('taskBeforePredecessor_SF');
            }
            else if (violationType === 'taskAfterPredecessor_SF') {
                topContentText = this.parent.localeObj.getConstant('taskAfterPredecessor_SF');
            }
            topContentText = formatString(topContentText, recordArgs);
        }
        const topContent = '<div id="' + ganttId + '_ValidationText">' + topContentText + '</div>';
        const cancelText = this.parent.localeObj.getConstant('cancelLink');
        const removeLinkTextTemplate = this.parent.localeObj.getConstant('removeLink');
        const removeLinkText = formatString(removeLinkTextTemplate, [recordName, recordNewStartDate]);
        const removeConstraintTextTemplate = this.parent.localeObj.getConstant('removeConstraint');
        violationType = this.formatViolationType(violationType);
        const removeConstraintText = formatString(removeConstraintTextTemplate, [violationType, recordName]);
        const preserveLinkTextTemplate = this.parent.localeObj.getConstant('preserveLink');
        const preserveLinkText = formatString(preserveLinkTextTemplate, [recordName, recordNewStartDate]);
        const innerTable = `
        <table>
            <tr><td><input type="radio" id="${ganttId}_ValidationCancel" name="ValidationRule" checked/></td></tr>
            <tr><td><input type="radio" id="${ganttId}_ValidationRemoveline" name="ValidationRule"/></td></tr>
            <tr><td><input type="radio" id="${ganttId}_RemoveConstraint" name="ValidationRule"/></td></tr>
            <tr><td><input type="radio" id="${ganttId}_ValidationAddlineOffset" name="ValidationRule"/></td></tr>
        </table>`;
        contentdiv.innerHTML = topContent + innerTable;
        const cancelInput = contentdiv.querySelector(`#${ganttId}_ValidationCancel`);
        const removeLinkInput = contentdiv.querySelector(`#${ganttId}_ValidationRemoveline`);
        const removeConstraintInput = contentdiv.querySelector(`#${ganttId}_RemoveConstraint`);
        const preserveLinkInput = contentdiv.querySelector(`#${ganttId}_ValidationAddlineOffset`);
        if (cancelInput) {
            new RadioButton({
                label: cancelText,
                name: 'ValidationRule',
                value: 'cancel',
                checked: true,
                cssClass: cls.constraintLabel
            }).appendTo(cancelInput);
        }
        if (removeLinkInput) {
            new RadioButton({
                label: removeLinkText,
                name: 'ValidationRule',
                value: 'removeLink',
                cssClass: cls.constraintLabel
            }).appendTo(removeLinkInput);
        }
        if (removeConstraintInput) {
            new RadioButton({
                label: removeConstraintText,
                name: 'ValidationRule',
                value: 'removeConstraint',
                cssClass: cls.constraintLabel
            }).appendTo(removeConstraintInput);
        }
        if (preserveLinkInput) {
            new RadioButton({
                label: preserveLinkText,
                name: 'ValidationRule',
                value: 'preserveLink',
                cssClass: cls.constraintLabel
            }).appendTo(preserveLinkInput);
        }
        const radioWrapper = contentdiv.querySelectorAll('.e-radio-wrapper');
        radioWrapper.forEach((wrapperElement) => {
            wrapperElement.style.padding = '6px';
            wrapperElement.querySelector('span').style.fontWeight = 'normal';
        });
        return contentdiv;
    }
    /**
     * To open constraint validation dialog
     *
     * @param {object} args - { violationType: string, parentRecord: IGanttData, record: IGanttData }
     * @returns {void}
     * @private
     */
    openConstraintValidationDialog(args) {
        const ganttObj = this.parent;
        const contentTemplate = this.constraintValidationDialogTemplate(args);
        const constraintConflictHeader = ganttObj.localeObj.getConstant('schedulingConflicts');
        ganttObj.validationDialogElement.setProperties({
            content: contentTemplate,
            header: constraintConflictHeader
        });
        const contentId = ganttObj.element.id + '_dialogValidationRule_dialog-content';
        const contentElement = ganttObj.validationDialogElement.element.querySelector('#' + contentId);
        contentElement.style.paddingTop = '10px';
        const headerId = ganttObj.element.id + '_dialogValidationRule_dialog-header';
        const headerElement = ganttObj.validationDialogElement.element.querySelector('#' + headerId);
        headerElement.style.padding = '8px 16px';
        this.updateZIndex(ganttObj);
        ganttObj.validationDialogElement.show();
    }
    /**
     * Constraint validation dialog template
     *
     * @param {object} args - { violationType: string, parentRecord: IGanttData, record: IGanttData }
     * @returns {HTMLElement} The HTML element representing the constraint validation dialog
     * @private
     */
    constraintValidationDialogTemplate(args) {
        const ganttId = this.parent.element.id;
        const { parentRecord, record, predecessorLink } = args;
        let { violationType } = args;
        const contentDiv = createElement('div', { className: 'e-ValidationContent' });
        const recordName = record.ganttProperties.taskName;
        let parentName = '';
        if (parentRecord && parentRecord.ganttProperties && parentRecord.ganttProperties.taskName) {
            parentName = parentRecord.ganttProperties.taskName;
        }
        const removeConstraintTemplate = this.parent.localeObj.getConstant('removeConstraint');
        const removeDependencyTemplate = this.parent.localeObj.getConstant('removeDependency');
        const cancelChangeText = this.parent.localeObj.getConstant('cancelChange');
        violationType = this.formatViolationType(violationType);
        const removeConstraintText = formatString(removeConstraintTemplate, [violationType, recordName]);
        const removeDependencyText = formatString(removeDependencyTemplate, [parentName, recordName]);
        let innerHtml = '<table>';
        innerHtml += `
        <tr>
            <td>
                <input type="radio" id="${ganttId}_RemoveConstraint" name="ValidationRule" />
            </td>
        </tr>`;
        if (predecessorLink !== undefined) {
            innerHtml += `
        <tr>
            <td>
                <input type="radio" id="${ganttId}_RemoveDependency" name="ValidationRule"/>
            </td>
        </tr>`;
        }
        innerHtml += `
        <tr>
            <td>
                <input type="radio" id="${ganttId}_CancelChange" name="ValidationRule"/>
            </td>
        </tr>`;
        innerHtml += '</table>';
        contentDiv.innerHTML = innerHtml;
        const removeConstraintElem = contentDiv.querySelector(`#${ganttId}_RemoveConstraint`);
        if (removeConstraintElem) {
            new RadioButton({
                label: removeConstraintText,
                name: 'ValidationRule',
                value: 'removeConstraint',
                checked: true,
                cssClass: cls.constraintLabel
            }).appendTo(removeConstraintElem);
        }
        if (predecessorLink !== undefined) {
            const removeDependencyElem = contentDiv.querySelector(`#${ganttId}_RemoveDependency`);
            if (removeDependencyElem) {
                new RadioButton({
                    label: removeDependencyText,
                    name: 'ValidationRule',
                    value: 'removeDependency',
                    cssClass: cls.constraintLabel
                }).appendTo(removeDependencyElem);
            }
        }
        const cancelChangeElem = contentDiv.querySelector(`#${ganttId}_CancelChange`);
        if (cancelChangeElem) {
            new RadioButton({
                label: cancelChangeText,
                name: 'ValidationRule',
                value: 'cancelChange',
                cssClass: cls.constraintLabel
            }).appendTo(cancelChangeElem);
        }
        const radioWrapper = contentDiv.querySelectorAll('.e-radio-wrapper');
        radioWrapper.forEach((wrapperElement) => {
            wrapperElement.style.padding = '6px';
            wrapperElement.querySelector('span').style.fontWeight = 'normal';
        });
        return contentDiv;
    }
    /**
     * To validate the types while editing the taskbar
     *
     * @param {IGanttData} ganttRecord .
     * @param {any} data .
     * @returns {boolean} .
     * @private
     */
    validateTypes(ganttRecord, data) {
        const predecessor = this.parent.predecessorModule.getValidPredecessor(ganttRecord);
        let parentGanttRecord;
        this.validationPredecessor = [];
        let violatedParent;
        let ganttTaskData;
        let violateType;
        const calendarContext = ganttRecord && ganttRecord.ganttProperties && ganttRecord.ganttProperties.calendarContext
            ? ganttRecord.ganttProperties.calendarContext
            : this.parent.defaultCalendarContext;
        const startDate = this.parent.predecessorModule.getPredecessorDate(ganttRecord, predecessor, null);
        if (data) {
            ganttTaskData = data.ganttProperties;
        }
        else {
            ganttTaskData = ganttRecord.ganttProperties;
        }
        const endDate = this.parent.allowUnscheduledTasks && isNullOrUndefined(startDate) ?
            ganttTaskData.endDate :
            this.dateValidateModule.getEndDate(startDate, ganttTaskData.duration, ganttTaskData.durationUnit, ganttTaskData, false);
        for (let i = 0; i < predecessor.length; i++) {
            parentGanttRecord = this.parent.connectorLineModule.getRecordByID(predecessor[i].from);
            let violationType = null;
            if (predecessor[i].type === 'FS') {
                const duration = this.parent.dateValidationModule.getDuration(startDate, ganttTaskData.startDate, this.parent.durationUnit, ganttTaskData.isAutoSchedule, ganttTaskData.isMilestone, true, calendarContext);
                if (duration !== 0 || (duration === 0 && this.parent.updateOffsetOnTaskbarEdit === false)) {
                    if (ganttTaskData.startDate < startDate) {
                        this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                        violationType = 'taskBeforePredecessor_FS';
                    }
                    else if (ganttTaskData.startDate > startDate) {
                        this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                        violationType = 'taskAfterPredecessor_FS';
                    }
                }
            }
            else if (predecessor[i].type === 'SS') {
                const endDateOlny = new Date(ganttTaskData.endDate);
                const startDateOlny = new Date(startDate);
                if (ganttTaskData.startDate < startDate) {
                    this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                    violationType = 'taskBeforePredecessor_SS';
                }
                else if (ganttTaskData.startDate > startDate) {
                    this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                    violationType = 'taskAfterPredecessor_SS';
                }
                else if (this.parent.allowUnscheduledTasks &&
                    isNullOrUndefined(ganttTaskData.startDate) &&
                    isNullOrUndefined(ganttTaskData.duration) &&
                    endDateOlny.setHours(0, 0, 0, 0) < startDateOlny.setHours(0, 0, 0, 0)) {
                    this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                    violationType = 'taskBeforePredecessor_SS';
                }
                else if (this.parent.allowUnscheduledTasks &&
                    isNullOrUndefined(ganttTaskData.startDate) &&
                    isNullOrUndefined(ganttTaskData.duration) &&
                    endDateOlny.setHours(0, 0, 0, 0) > startDateOlny.setHours(0, 0, 0, 0)) {
                    this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                    violationType = 'taskAfterPredecessor_SS';
                }
            }
            else if (predecessor[i].type === 'FF') {
                const duration = this.parent.dateValidationModule.getDuration(startDate, parentGanttRecord.ganttProperties.endDate, this.parent.durationUnit, parentGanttRecord.ganttProperties.isAutoSchedule, parentGanttRecord.ganttProperties.isMilestone, true, calendarContext);
                if (duration !== 0 || (duration === 0 && this.parent.updateOffsetOnTaskbarEdit === false)) {
                    if (endDate <= parentGanttRecord.ganttProperties.endDate) {
                        this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                        violationType = 'taskBeforePredecessor_FF';
                    }
                    else if (endDate > parentGanttRecord.ganttProperties.endDate) {
                        this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                        violationType = 'taskAfterPredecessor_FF';
                    }
                }
            }
            else if (predecessor[i].type === 'SF') {
                const duration = this.parent.dateValidationModule.getDuration(parentGanttRecord.ganttProperties.startDate, endDate, this.parent.durationUnit, parentGanttRecord.ganttProperties.isAutoSchedule, parentGanttRecord.ganttProperties.isMilestone, true, calendarContext);
                if (duration !== 0 || (duration === 0 && this.parent.updateOffsetOnTaskbarEdit === false)) {
                    if (endDate < parentGanttRecord.ganttProperties.startDate) {
                        this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                        violationType = 'taskBeforePredecessor_SF';
                    }
                    else if (endDate >= parentGanttRecord.ganttProperties.startDate) {
                        this.validationPredecessor.push(predecessor[parseInt(i.toString(), 10)]);
                        violationType = 'taskAfterPredecessor_SF';
                    }
                }
            }
            if (!isNullOrUndefined(violationType) && isNullOrUndefined(violateType)) {
                violatedParent = parentGanttRecord;
                violateType = violationType;
            }
        }
        if (!violateType && this.parent.constraintViolationType) {
            violateType = this.parent.constraintViolationType;
            const predecessorsCollection = ganttRecord.ganttProperties.predecessor;
            const currentTaskId = this.parent.viewType === 'ResourceView' ? ganttRecord.ganttProperties.taskId.toString()
                : ganttRecord.ganttProperties.rowUniqueID.toString();
            const predecessors = predecessorsCollection.filter((data) => {
                if (data.to === currentTaskId) {
                    return data;
                }
                else {
                    return null;
                }
            });
            let maxViolation = -Infinity;
            if (predecessors.length > 0) {
                for (let i = 0; i < predecessors.length; i++) {
                    const fromId = predecessors[i].from;
                    const predecessorRecord = this.parent.getRecordByID(fromId);
                    if (predecessorRecord && predecessorRecord.ganttProperties.endDate && ganttRecord.ganttProperties.startDate) {
                        const violation = predecessorRecord.ganttProperties.endDate.getTime() - ganttRecord.ganttProperties.startDate.getTime();
                        if (violation > maxViolation) {
                            maxViolation = violation;
                            violatedParent = predecessorRecord;
                        }
                    }
                }
            }
        }
        const validateArgs = {
            parentTask: violatedParent,
            task: ganttRecord,
            violationType: violateType
        };
        return validateArgs;
    }
    /**
     * Method to remove and update new predecessor collection in successor record
     *
     * @param {IGanttData} data .
     * @returns {void} .
     * @private
     */
    addRemovePredecessor(data) {
        const prevData = this.parent.previousRecords[data.uniqueID];
        const newPredecessor = !isNullOrUndefined(data.ganttProperties.predecessor) ?
            data.ganttProperties.predecessor.slice() : data.ganttProperties.predecessor;
        // eslint-disable-next-line
        if (prevData && prevData.ganttProperties && prevData.ganttProperties.hasOwnProperty('predecessor')) {
            const prevPredecessor = prevData.ganttProperties.predecessor;
            if (!isNullOrUndefined(prevPredecessor)) {
                for (let p = 0; p < prevPredecessor.length; p++) {
                    const parentGanttRecord = this.parent.connectorLineModule.getRecordByID(prevPredecessor[parseInt(p.toString(), 10)].from);
                    if (parentGanttRecord === data) {
                        const isValid = data.ganttProperties.predecessor.filter((pred) => {
                            return prevPredecessor[p].from === pred.from && prevPredecessor[p].to === pred.to;
                        });
                        if (isValid.length === 0) {
                            if (data.parentItem && this.parent.taskFields.dependency && data.ganttProperties.predecessor &&
                                this.parent.allowParentDependency) {
                                if (prevPredecessor[p].from !== data.parentItem.taskId &&
                                    prevPredecessor[p].to !== data.parentItem.taskId) {
                                    data.ganttProperties.predecessor.push(prevPredecessor[parseInt(p.toString(), 10)]);
                                }
                            }
                            else {
                                data.ganttProperties.predecessor.push(prevPredecessor[parseInt(p.toString(), 10)]);
                            }
                        }
                    }
                    else {
                        const parentPredecessor = extend([], [], parentGanttRecord.ganttProperties.predecessor, true);
                        const parentIndex = getIndex(prevPredecessor[parseInt(p.toString(), 10)], 'from', parentPredecessor, 'to');
                        if (parentIndex !== -1) {
                            parentPredecessor.splice(parentIndex, 1);
                            this.parent.setRecordValue('predecessor', parentPredecessor, parentGanttRecord.ganttProperties, true);
                        }
                    }
                }
            }
            if (!isNullOrUndefined(newPredecessor)) {
                for (let n = 0; n < newPredecessor.length; n++) {
                    const parentGanttRecord = this.parent.connectorLineModule.getRecordByID(newPredecessor[parseInt(n.toString(), 10)].from);
                    const parentPredecessor = extend([], [], parentGanttRecord.ganttProperties.predecessor, true);
                    const isValid = parentPredecessor.filter((pred) => {
                        return newPredecessor[n].from === pred.from && newPredecessor[n].to === pred.to;
                    });
                    if (isValid.length === 0) {
                        parentPredecessor.push(newPredecessor[parseInt(n.toString(), 10)]);
                        this.parent.setRecordValue('predecessor', parentPredecessor, parentGanttRecord.ganttProperties, true);
                    }
                }
            }
        }
    }
    /**
     * Method to remove a predecessor from a record.
     *
     * @param {IGanttData} childRecord .
     * @param {number} index .
     * @returns {void} .
     * @private
     */
    removePredecessorByIndex(childRecord, index) {
        if (this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed']) {
            if (this.parent.undoRedoModule['redoEnabled']) {
                this.parent.undoRedoModule['disableRedo']();
            }
            this.parent.undoRedoModule['createUndoCollection']();
            const details = {};
            details['action'] = 'DeleteDependency';
            details['modifiedRecords'] = extend([], [childRecord], [], true);
            this.parent.undoRedoModule['getUndoCollection'][this.parent.undoRedoModule['getUndoCollection'].length - 1] = details;
        }
        const childPredecessor = childRecord.ganttProperties.predecessor;
        const predecessor = childPredecessor.splice(index, 1);
        const parentRecord = this.parent.connectorLineModule.getRecordByID(predecessor[0].from);
        const parentPredecessor = parentRecord.ganttProperties.predecessor;
        const parentIndex = getIndex(predecessor[0], 'from', parentPredecessor, 'to');
        parentPredecessor.splice(parentIndex, 1);
        const predecessorString = this.parent.predecessorModule.getPredecessorStringValue(childRecord);
        childPredecessor.push(predecessor[0]);
        this.parent.connectorLineEditModule.updatePredecessor(childRecord, predecessorString);
    }
    /**
     * To render predecessor delete confirmation dialog
     *
     * @returns {void} .
     * @private
     */
    renderPredecessorDeleteConfirmDialog() {
        this.confirmPredecessorDialog = new Dialog({
            width: '320px',
            isModal: true,
            enableRtl: this.parent.enableRtl,
            content: this.parent.localeObj.getConstant('confirmPredecessorDelete'),
            buttons: [
                {
                    click: this.confirmOkDeleteButton.bind(this),
                    buttonModel: { content: this.parent.localeObj.getConstant('okText'), isPrimary: true }
                },
                {
                    click: this.confirmCloseDialog.bind(this),
                    buttonModel: { content: this.parent.localeObj.getConstant('cancel') }
                }
            ],
            target: this.parent.element,
            animationSettings: { effect: 'None' }
        });
        const confirmDialog = createElement('div', {
            id: this.parent.element.id + '_deletePredecessorConfirmDialog'
        });
        this.parent.element.appendChild(confirmDialog);
        this.confirmPredecessorDialog.isStringTemplate = true;
        this.confirmPredecessorDialog.appendTo(confirmDialog);
    }
    confirmCloseDialog() {
        this.confirmPredecessorDialog.destroy();
    }
    confirmOkDeleteButton() {
        this.removePredecessorByIndex(this.childRecord, this.predecessorIndex);
        this.confirmPredecessorDialog.destroy();
    }
}
